import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { pmsFrom } from '@/integrations/pms';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { CalendarDays, MapPin, Clock, Video, Users, ExternalLink, Check, ChevronLeft, ChevronRight, ArrowLeft } from 'lucide-react';
import { format, isFuture } from 'date-fns';
import { SEOHead } from '@/components/seo/SEOHead';
import { toast } from 'sonner';
import { DynamicForm } from '@/components/common/DynamicForm';

export default function EventDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const queryClient = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [showCustomFormModal, setShowCustomFormModal] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', notes: '' });
  const [submitted, setSubmitted] = useState(false);

  const { data: event, isLoading } = useQuery({
    queryKey: ['public-event', slug],
    queryFn: async () => {
      const { data, error } = await pmsFrom('events')
        .select('*')
        .eq('slug', slug)
        .eq('is_published', true)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!slug,
  });

  // Fetch all published events for prev/next navigation
  const { data: allEvents = [] } = useQuery({
    queryKey: ['public-events-nav'],
    queryFn: async () => {
      const { data, error } = await pmsFrom('events')
        .select('id, slug, title, start_date')
        .eq('is_published', true)
        .order('start_date', { ascending: true });
      if (error) throw error;
      return data;
    },
  });

  const currentIndex = allEvents.findIndex(e => e.slug === slug);
  const prevEvent = currentIndex > 0 ? allEvents[currentIndex - 1] : null;
  const nextEvent = currentIndex < allEvents.length - 1 ? allEvents[currentIndex + 1] : null;

  const registerMutation = useMutation({
    mutationFn: async () => {
      const { error } = await pmsFrom('event_registrations').insert({
        event_id: event!.id,
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        notes: formData.notes || null,
      });
      if (error) throw error;
      await pmsFrom('events').update({
        registration_count: (event!.registration_count || 0) + 1,
      }).eq('id', event!.id);
    },
    onSuccess: () => {
      setSubmitted(true);
      queryClient.invalidateQueries({ queryKey: ['public-event', slug] });
      toast.success('Registration confirmed!');
    },
    onError: (err) => toast.error('Registration failed', { description: String(err) }),
  });

  if (isLoading) {
    return <div className="container mx-auto px-4 py-16 text-center text-muted-foreground">Loading…</div>;
  }

  if (!event) {
    return (
      <div className="container mx-auto px-4 py-16 text-center">
        <h1 className="text-2xl font-bold">Event Not Found</h1>
        <p className="text-muted-foreground mt-2">This event may have ended or been removed.</p>
        <Link to="/events">
          <Button variant="outline" className="mt-4 gap-2"><ArrowLeft className="h-4 w-4" /> Back to Events</Button>
        </Link>
      </div>
    );
  }

  const isUpcoming = isFuture(new Date(event.end_date || event.start_date));
  const isFull = event.max_attendees ? (event.registration_count ?? 0) >= event.max_attendees : false;

  const eventJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: event.title,
    description: event.excerpt || event.description,
    startDate: event.start_date,
    endDate: event.end_date || event.start_date,
    eventStatus: 'https://schema.org/EventScheduled',
    eventAttendanceMode: event.is_virtual
      ? 'https://schema.org/OnlineEventAttendanceMode'
      : 'https://schema.org/OfflineEventAttendanceMode',
    ...(event.venue_name && !event.is_virtual && {
      location: {
        '@type': 'Place',
        name: event.venue_name,
        address: {
          '@type': 'PostalAddress',
          streetAddress: event.venue_address,
          addressLocality: event.venue_city,
          addressRegion: event.venue_state,
          postalCode: event.venue_zip,
        },
      },
    }),
    ...(event.is_virtual && event.virtual_url && {
      location: { '@type': 'VirtualLocation', url: event.virtual_url },
    }),
    ...(event.featured_image && { image: event.featured_image }),
    isAccessibleForFree: event.is_free,
    ...(!event.is_free && event.ticket_price && event.ticket_price > 0 && {
      offers: {
        '@type': 'Offer',
        price: event.ticket_price,
        priceCurrency: 'USD',
        availability: isFull ? 'https://schema.org/SoldOut' : 'https://schema.org/InStock',
      },
    }),
  };

  return (
    <>
      <SEOHead
        title={event.meta_title || event.title}
        description={event.meta_description || event.excerpt || ''}
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(eventJsonLd) }} />

      <article className="container mx-auto px-4 py-12 max-w-4xl">
        {/* Back link */}
        <Link to="/events" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors">
          <ArrowLeft className="h-3.5 w-3.5" /> All Events
        </Link>

        {/* Header */}
        {event.featured_image && (
          <img src={event.featured_image} alt={event.title} className="w-full h-64 object-cover rounded-xl mb-8" />
        )}

        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 mb-2">
              {event.category && <Badge variant="secondary" className="capitalize">{event.category}</Badge>}
              {event.is_virtual && <Badge variant="outline"><Video className="h-3 w-3 mr-1" />Virtual</Badge>}
              {!isUpcoming && <Badge variant="secondary">Past Event</Badge>}
            </div>
            <h1 className="text-3xl md:text-4xl font-bold">{event.title}</h1>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {/* Main content */}
          <div className="md:col-span-2 space-y-6">
            {event.description && (
              <div className="prose prose-lg max-w-none dark:prose-invert whitespace-pre-wrap">
                {event.description}
              </div>
            )}
            {event.content && (
              <div className="prose max-w-none dark:prose-invert whitespace-pre-wrap">
                {event.content}
              </div>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            <Card>
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <CalendarDays className="h-4 w-4 text-primary" />
                  <span className="font-medium">{format(new Date(event.start_date), 'EEEE, MMMM d, yyyy')}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="h-4 w-4 text-primary" />
                  <span>
                    {event.all_day ? 'All Day' : format(new Date(event.start_date), 'h:mm a')}
                    {event.end_date && !event.all_day && ` – ${format(new Date(event.end_date), 'h:mm a')}`}
                  </span>
                </div>
                {event.venue_name && !event.is_virtual && (
                  <div className="flex items-start gap-2 text-sm">
                    <MapPin className="h-4 w-4 text-primary mt-0.5" />
                    <div>
                      <p className="font-medium">{event.venue_name}</p>
                      {event.venue_address && <p className="text-muted-foreground">{event.venue_address}</p>}
                      {event.venue_city && (
                        <p className="text-muted-foreground">{event.venue_city}, {event.venue_state} {event.venue_zip}</p>
                      )}
                    </div>
                  </div>
                )}
                {event.is_virtual && event.virtual_url && isUpcoming && (
                  <a href={event.virtual_url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-primary hover:underline">
                    <Video className="h-4 w-4" /> Join Virtual Event <ExternalLink className="h-3 w-3" />
                  </a>
                )}
                {!event.is_free && event.ticket_price && event.ticket_price > 0 && (
                  <div className="text-lg font-semibold">${event.ticket_price.toFixed(2)}</div>
                )}
                {event.is_free && <Badge variant="secondary">Free Event</Badge>}
              </CardContent>
            </Card>

            {/* Registration */}
            {event.registration_enabled && isUpcoming && (
              <>
                {/* Custom form - inline mode */}
                {event.registration_form_id && (event as any).form_display_mode === 'inline' ? (
                  <Card>
                    <CardContent className="p-4">
                      <h3 className="font-semibold mb-3">Register</h3>
                      <DynamicForm formId={event.registration_form_id} pageSlug={`event:${event.slug}`} />
                    </CardContent>
                  </Card>
                ) : (
                  <Card>
                    <CardContent className="p-4">
                      {submitted ? (
                        <div className="text-center py-4">
                          <Check className="h-8 w-8 text-green-500 mx-auto mb-2" />
                          <p className="font-medium">You're registered!</p>
                          <p className="text-sm text-muted-foreground">We'll send details to your email.</p>
                        </div>
                      ) : event.registration_url ? (
                        <a href={event.registration_url} target="_blank" rel="noopener noreferrer">
                          <Button className="w-full">Register Now <ExternalLink className="h-4 w-4 ml-1" /></Button>
                        </a>
                      ) : isFull ? (
                        <div className="text-center py-2">
                          <p className="font-medium text-muted-foreground">This event is full</p>
                        </div>
                      ) : event.registration_form_id && (event as any).form_display_mode !== 'inline' ? (
                        /* Custom form - modal mode */
                        <div>
                          {event.max_attendees && (
                            <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                              <Users className="h-3 w-3" />
                              {event.max_attendees - (event.registration_count ?? 0)} spots remaining
                            </p>
                          )}
                          <Button className="w-full" onClick={() => setShowCustomFormModal(true)}>Register Now</Button>
                        </div>
                      ) : !showForm ? (
                        <div>
                          {event.max_attendees && (
                            <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                              <Users className="h-3 w-3" />
                              {event.max_attendees - (event.registration_count ?? 0)} spots remaining
                            </p>
                          )}
                          <Button className="w-full" onClick={() => setShowForm(true)}>Register Now</Button>
                        </div>
                      ) : (
                        <form onSubmit={e => { e.preventDefault(); registerMutation.mutate(); }} className="space-y-3">
                          <div><Label>Name *</Label><Input required value={formData.name} onChange={e => setFormData(p => ({ ...p, name: e.target.value }))} /></div>
                          <div><Label>Email *</Label><Input required type="email" value={formData.email} onChange={e => setFormData(p => ({ ...p, email: e.target.value }))} /></div>
                          <div><Label>Phone</Label><Input value={formData.phone} onChange={e => setFormData(p => ({ ...p, phone: e.target.value }))} /></div>
                          <div><Label>Notes</Label><Textarea rows={2} value={formData.notes} onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))} /></div>
                          <Button type="submit" className="w-full" disabled={registerMutation.isPending}>
                            {registerMutation.isPending ? 'Registering…' : 'Confirm Registration'}
                          </Button>
                        </form>
                      )}
                    </CardContent>
                  </Card>
                )}

                {/* Custom form modal */}
                {event.registration_form_id && (
                  <Dialog open={showCustomFormModal} onOpenChange={setShowCustomFormModal}>
                    <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>Register for {event.title}</DialogTitle>
                      </DialogHeader>
                      <DynamicForm formId={event.registration_form_id} pageSlug={`event:${event.slug}`} />
                    </DialogContent>
                  </Dialog>
                )}
              </>
            )}
          </div>
        </div>

        {/* Prev / Next navigation */}
        <div className="flex items-center justify-between mt-12 pt-8 border-t">
          {prevEvent ? (
            <Link to={`/events/${prevEvent.slug}`} className="flex items-center gap-2 text-sm hover:text-primary transition-colors group">
              <ChevronLeft className="h-4 w-4 group-hover:-translate-x-0.5 transition-transform" />
              <div className="text-right">
                <span className="text-xs text-muted-foreground block">Previous Event</span>
                <span className="font-medium">{prevEvent.title}</span>
              </div>
            </Link>
          ) : <div />}
          {nextEvent ? (
            <Link to={`/events/${nextEvent.slug}`} className="flex items-center gap-2 text-sm hover:text-primary transition-colors group text-right">
              <div>
                <span className="text-xs text-muted-foreground block">Next Event</span>
                <span className="font-medium">{nextEvent.title}</span>
              </div>
              <ChevronRight className="h-4 w-4 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          ) : <div />}
        </div>
      </article>
    </>
  );
}
